import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { router } from '@inertiajs/react'
import { Search, Plus, Cable, Loader2 } from 'lucide-react'
import confetti from 'canvas-confetti'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import IntegrationConnectModal from '@/frontend/components/DigitalWorkers/IntegrationConnectModal'
import GoogleAccountsModal from '@/frontend/components/DigitalWorkers/GoogleAccountsModal'
import MetaAccountsModal from '@/frontend/components/DigitalWorkers/MetaAccountsModal'
import { PROVIDER_ICONS_SMALL, FallbackIcon } from '@/frontend/components/DigitalWorkers/providerIcons'
import WorkerIntegrationRow from './WorkerIntegrationRow'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import { submitComposioConnect } from '@/frontend/utils/composioConnect'
import {
  submitComposioRefresh,
} from '@/frontend/utils/composioRefresh'
import { isComposioIntegration } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function celebrateConnection(element: HTMLElement | null) {
  if (!element || prefersReducedMotion()) return
  const rect = element.getBoundingClientRect()
  const origin = {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: (rect.top + rect.height / 2) / window.innerHeight,
  }
  confetti({
    particleCount: 28,
    spread: 60,
    startVelocity: 22,
    origin,
    colors: ['#8b5cf6', '#d946ef', '#ec4899', '#a78bfa'],
    scalar: 0.75,
    ticks: 140,
  })
}
import type {
  AvailableIntegration,
  BuiltinOrgIntegration,
  ComposioOrgIntegration,
  DigitalWorkerDetail,
  GoogleAccountOption,
  IntegrationToolkit,
  OrgIntegration,
} from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  worker: DigitalWorkerDetail
  integrations: OrgIntegration[]
  availableIntegrations?: AvailableIntegration[]
  integrationToolkits?: IntegrationToolkit[]
  integrationCategories?: string[]
  googleAdsAccounts: GoogleAccountOption[]
  googleAnalyticsProperties: GoogleAccountOption[]
  metaAdAccounts: GoogleAccountOption[]
  canManage: boolean
  initialModalToOpen?: 'google_accounts' | 'meta_accounts' | null
  openConnectModalSignal?: number
  embedded?: boolean
  hideHeader?: boolean
  loading?: boolean
  error?: string | null
}

interface PopupIntegrationMessage {
  type: 'stationzero:digital-worker-integration'
  status: 'success' | 'error'
  workerId?: string | null
  provider?: string | null
  openModal?: 'google_accounts' | 'meta_accounts' | null
  warning?: string | null
}

const CONNECT_PATHS: Record<string, string> = {
  meta: '/dashboard/integrations/connect_facebook',
  google: '/dashboard/integrations/connect_google',
  notion: '/dashboard/integrations/connect_notion',
  slack: '/dashboard/integrations/connect_slack',
}

const INTEGRATION_RELOAD_PROPS = [
  'all_org_integrations',
  'available_integrations',
  'integration_toolkits',
  'google_ads_accounts',
  'google_analytics_properties',
  'meta_ad_accounts',
  'flash',
]
const COMPOSIO_RELOAD_INTERVAL_MS = 3000
const COMPOSIO_PENDING_TIMEOUT_MS = 120000

function openOauthPopup(url: string, popupName: string) {
  window.open(url, popupName, 'popup,width=720,height=820,resizable=yes,scrollbars=yes')
}

function scheduleBusyClear(
  key: string,
  setBusyAction: Dispatch<SetStateAction<string | null>>,
) {
  window.setTimeout(() => {
    setBusyAction((current) => (current === key ? null : current))
  }, 800)
}

export default function IntegrationsSection({
  worker,
  integrations,
  availableIntegrations = [],
  integrationToolkits = [],
  integrationCategories = [],
  googleAdsAccounts,
  googleAnalyticsProperties,
  metaAdAccounts,
  canManage,
  initialModalToOpen,
  openConnectModalSignal = 0,
  embedded = false,
  hideHeader = false,
  loading = false,
  error = null,
}: Props) {
  const [googleModalOpen, setGoogleModalOpen] = useState(false)
  const [metaModalOpen, setMetaModalOpen] = useState(false)
  const [connectModalOpen, setConnectModalOpen] = useState(false)
  const [disconnectingIds, setDisconnectingIds] = useState<Set<string>>(new Set())
  const [refreshingAccountId, setRefreshingAccountId] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [justConnectedIds, setJustConnectedIds] = useState<Set<string>>(new Set())
  const [pendingToolkitSlug, setPendingToolkitSlug] = useState<string | null>(null)
  const previousIdsRef = useRef<Set<string>>(new Set(integrations.map((integration) => integration.id)))
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const firstRenderRef = useRef(true)

  const reloadIntegrationProps = useCallback((onSuccess?: () => void) => {
    router.reload({
      only: INTEGRATION_RELOAD_PROPS,
      onSuccess,
    })
  }, [])

  useEffect(() => {
    const currentIds = integrations.map((integration) => integration.id)
    if (firstRenderRef.current) {
      firstRenderRef.current = false
      previousIdsRef.current = new Set(currentIds)
      return
    }

    const newlyAdded = currentIds.filter((id) => !previousIdsRef.current.has(id))
    previousIdsRef.current = new Set(currentIds)

    if (newlyAdded.length === 0) return

    setJustConnectedIds((previous) => {
      const next = new Set(previous)
      newlyAdded.forEach((id) => next.add(id))
      return next
    })

    newlyAdded.forEach((id) => {
      window.setTimeout(() => {
        celebrateConnection(rowRefs.current.get(id) ?? null)
      }, 120)
    })

    const cleanupTimer = window.setTimeout(() => {
      setJustConnectedIds((previous) => {
        if (previous.size === 0) return previous
        const next = new Set(previous)
        newlyAdded.forEach((id) => next.delete(id))
        return next
      })
    }, 2600)

    return () => window.clearTimeout(cleanupTimer)
  }, [integrations])

  useEffect(() => {
    if (!canManage) return
    if (initialModalToOpen === 'google_accounts') setGoogleModalOpen(true)
    else if (initialModalToOpen === 'meta_accounts') setMetaModalOpen(true)
  }, [canManage, initialModalToOpen])

  useEffect(() => {
    if (!openConnectModalSignal) return
    if (!canManage) return
    if (availableIntegrations.length === 0 && integrationToolkits.length === 0) return

    setConnectModalOpen(true)
  }, [availableIntegrations.length, canManage, integrationToolkits.length, openConnectModalSignal])

  useEffect(() => {
    const handleMessage = (event: MessageEvent<PopupIntegrationMessage>) => {
      if (event.origin !== window.location.origin) return
      const payload = event.data
      if (!payload || payload.type !== 'stationzero:digital-worker-integration') return
      if (String(payload.workerId) !== String(worker.id)) return

      setBusyAction(null)

      if (payload.status !== 'success') {
        if (payload.provider) setPendingToolkitSlug((current) => (current === payload.provider ? null : current))
        setConnectError('Não foi possível concluir a conexão. Tente novamente.')
        return
      }

      if (payload.warning) {
        setConnectError(payload.warning)
        setConnectModalOpen(false)
        reloadIntegrationProps()
        return
      }

      setConnectError(null)
      setConnectModalOpen(false)
      reloadIntegrationProps(() => {
        if (payload.provider) setPendingToolkitSlug((current) => (current === payload.provider ? null : current))
        setBusyAction(null)
        setConnectModalOpen(false)
        if (payload.openModal === 'google_accounts') {
          window.setTimeout(() => setGoogleModalOpen(true), 0)
        } else if (payload.openModal === 'meta_accounts') {
          window.setTimeout(() => setMetaModalOpen(true), 0)
        }
      })
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [reloadIntegrationProps, worker.id])

  useEffect(() => {
    if (!pendingToolkitSlug) return

    const connected = integrations.some((integration) => (
      isComposioIntegration(integration) &&
      integration.app_slug === pendingToolkitSlug &&
      integration.status === 'active'
    ))
    if (!connected) return

    setPendingToolkitSlug(null)
    setBusyAction(null)
    setConnectModalOpen(false)
  }, [integrations, pendingToolkitSlug])

  useEffect(() => {
    if (!pendingToolkitSlug) return

    const reloadIfVisible = () => {
      if (document.visibilityState === 'hidden') return
      reloadIntegrationProps()
    }

    window.addEventListener('focus', reloadIfVisible)
    document.addEventListener('visibilitychange', reloadIfVisible)
    const intervalId = window.setInterval(reloadIfVisible, COMPOSIO_RELOAD_INTERVAL_MS)
    const timeoutId = window.setTimeout(() => {
      setPendingToolkitSlug((current) => (current === pendingToolkitSlug ? null : current))
      setConnectError('A conexão demorou mais que o esperado. Verifique a janela aberta e tente novamente se necessário.')
    }, COMPOSIO_PENDING_TIMEOUT_MS)

    return () => {
      window.removeEventListener('focus', reloadIfVisible)
      document.removeEventListener('visibilitychange', reloadIfVisible)
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [pendingToolkitSlug, reloadIntegrationProps])

  const showSearch = integrations.length > 5
  const connectedProviders = useMemo(
    () => integrations.filter((integration) => !isComposioIntegration(integration)).map((integration) => integration.provider),
    [integrations],
  )

  useEffect(() => {
    if (!showSearch) setSearch('')
  }, [showSearch])

  const filteredIntegrations = useMemo(() => {
    if (!search.trim()) return integrations
    const query = search.toLowerCase()
    return integrations.filter((integration) => (
      integration.provider_label.toLowerCase().includes(query) ||
      integration.provider.toLowerCase().includes(query)
    ))
  }, [integrations, search])

  const finishDisconnect = (id: string) => {
    setDisconnectingIds((previous) => {
      const next = new Set(previous)
      next.delete(id)
      return next
    })
  }

  const handleAssignIntegration = (integration: AvailableIntegration) => {
    if (!integration.id) return

    const actionKey = `assign:${integration.id}`
    setBusyAction(actionKey)
    setConnectError(null)

    router.post(`/dashboard/digital_workers/${worker.id}/toggle_integration`, {
      integration_id: integration.id,
      enabled: true,
    }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setBusyAction(null)
        setConnectModalOpen(false)
        reloadIntegrationProps(() => {
          if (integration.provider === 'google') {
            window.setTimeout(() => setGoogleModalOpen(true), 0)
          } else if (integration.provider === 'meta') {
            window.setTimeout(() => setMetaModalOpen(true), 0)
          }
        })
      },
      onError: () => setBusyAction(null),
      onFinish: () => setBusyAction(null),
    })
  }

  const handleConnectProvider = (provider: string) => {
    const path = CONNECT_PATHS[provider]
    if (!path) return
    if (busyAction || pendingToolkitSlug) return

    const actionKey = `provider:${provider}`
    setBusyAction(actionKey)
    setConnectError(null)

    const url = new URL(path, window.location.origin)
    url.searchParams.set('digital_worker_id', worker.id)
    url.searchParams.set('popup', '1')
    openOauthPopup(url.toString(), `digital-worker-integration-${provider}`)
    scheduleBusyClear(actionKey, setBusyAction)
  }

  const handleConnectToolkit = (slug: string) => {
    if (busyAction || pendingToolkitSlug) return

    const actionKey = `toolkit:${slug}`
    setPendingToolkitSlug(slug)
    setBusyAction(actionKey)
    setConnectError(null)
    submitComposioConnect(slug, worker.id)
    scheduleBusyClear(actionKey, setBusyAction)
  }

  const handleDisconnect = (integration: OrgIntegration) => {
    if (integration.admin_management) return
    if (isComposioIntegration(integration)) {
      if (integration.admin_management) return
      if (!confirm(`Desconectar ${integration.provider_label}? As credenciais serão removidas.`)) return

      setDisconnectingIds((previous) => new Set(previous).add(integration.id))
      router.delete('/dashboard/composio/disconnect', {
        data: { connected_account_id: integration.connected_account_id },
        preserveScroll: true,
        preserveState: true,
        onError: () => alert('Não foi possível desconectar. Tente novamente.'),
        onFinish: () => finishDisconnect(integration.id),
      })
      return
    }

    const builtin = integration as BuiltinOrgIntegration

    if (builtin.provider === 'google' || builtin.provider === 'meta') {
      const providerName = builtin.provider === 'google' ? 'Google' : 'Meta'
      if (!confirm(`Ao desconectar ${providerName}, as contas selecionadas serão perdidas. Continuar?`)) return
    } else if (!confirm(`Desconectar ${builtin.provider_label}?`)) {
      return
    }

    setDisconnectingIds((previous) => new Set(previous).add(integration.id))
    router.post(`/dashboard/digital_workers/${worker.id}/toggle_integration`, {
      integration_id: integration.id,
      enabled: false,
    }, {
      preserveScroll: true,
      preserveState: true,
      onError: () => alert('Não foi possível desconectar. Tente novamente.'),
      onFinish: () => finishDisconnect(integration.id),
    })
  }

  const handleRefresh = (integration: ComposioOrgIntegration) => {
    if (integration.admin_management) return
    setRefreshingAccountId(integration.connected_account_id)
    submitComposioRefresh(integration.connected_account_id, {
      reloadProps: INTEGRATION_RELOAD_PROPS,
      onFinish: () => setRefreshingAccountId(null),
    })
  }

  const handleReconnect = (integration: ComposioOrgIntegration) => {
    if (integration.admin_management) return
    if (busyAction || pendingToolkitSlug) return

    const actionKey = `toolkit:${integration.app_slug}`
    setPendingToolkitSlug(integration.app_slug)
    setBusyAction(actionKey)
    setConnectError(null)
    submitComposioConnect(integration.app_slug, worker.id, {
      accountLabel: integration.account_label,
      replacesConnectedAccountId: integration.connected_account_id,
    })
    scheduleBusyClear(actionKey, setBusyAction)
  }

  const googleCount = googleAdsAccounts.filter((account) => account.selected).length +
    googleAnalyticsProperties.filter((property) => property.selected).length
  const metaCount = metaAdAccounts.filter((account) => account.selected).length
  const canOpenConnectModal = canManage && (availableIntegrations.length > 0 || integrationToolkits.length > 0)

  const searchControl = showSearch ? (
    <div className={embedded ? 'relative' : 'relative mb-3 -mt-1'}>
      <Search className={embedded
        ? 'absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground'
        : 'absolute left-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground'}
      />
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Buscar..."
        aria-label="Buscar integrações"
        className={embedded
          ? 'h-8 w-full pl-8 text-xs sm:w-40'
          : 'h-8 rounded-none border-0 border-b border-border bg-transparent pl-6 text-xs shadow-none focus-visible:border-border focus-visible:ring-0'}
      />
    </div>
  ) : null

  const addButton = canOpenConnectModal ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      data-testid="integrations-add-button"
      className="h-auto gap-1.5 px-0 py-0 text-muted-foreground hover:text-foreground"
      onClick={() => setConnectModalOpen(true)}
    >
      <Plus className="h-3.5 w-3.5" />
      Adicionar
    </Button>
  ) : null

  const quickAddItems = useMemo(() => {
    if (!canManage) return []

    const presets: Array<{ label: string; builtinProviders?: string[]; toolkitSlugs?: string[] }> = [
      { label: 'Meta', builtinProviders: ['meta'] },
      { label: 'Google', builtinProviders: ['google'] },
      { label: 'Notion', builtinProviders: ['notion'], toolkitSlugs: ['notion'] },
      { label: 'Google Sheets', toolkitSlugs: ['googlesheets', 'google_sheets'] },
    ]

    type QuickItem =
      | { kind: 'builtin'; key: string; label: string; integration: AvailableIntegration; actionKey: string }
      | { kind: 'toolkit'; key: string; label: string; toolkit: IntegrationToolkit; actionKey: string }

    return presets.flatMap<QuickItem>((preset) => {
      for (const provider of preset.builtinProviders ?? []) {
        if (connectedProviders.includes(provider)) return []
        const integration = availableIntegrations.find((candidate) => candidate.provider === provider)
        if (integration) {
          const actionKey = integration.status === 'assignable'
            ? `assign:${integration.id || integration.provider}`
            : `provider:${integration.provider}`
          return [{ kind: 'builtin', key: `builtin:${provider}`, label: preset.label, integration, actionKey }]
        }
      }

      for (const slug of preset.toolkitSlugs ?? []) {
        if (connectedProviders.includes(slug)) return []
        const toolkit = integrationToolkits.find((candidate) => candidate.slug === slug)
        if (toolkit) {
          return [{ kind: 'toolkit', key: `toolkit:${slug}`, label: preset.label, toolkit, actionKey: `toolkit:${slug}` }]
        }
      }

      return []
    })
  }, [canManage, availableIntegrations, integrationToolkits, connectedProviders])

  const quickAddSection = quickAddItems.length > 0 ? (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="integrations-quick-add">
      <span className="mr-1 text-xs font-medium text-muted-foreground">Rápido</span>
      {quickAddItems.map((item) => {
        const busy = busyAction === item.actionKey
        const disabled = Boolean(busyAction || pendingToolkitSlug)
        const handleClick = () => {
          if (item.kind === 'builtin') {
            if (item.integration.status === 'assignable') handleAssignIntegration(item.integration)
            else handleConnectProvider(item.integration.provider)
          } else {
            handleConnectToolkit(item.toolkit.slug)
          }
        }
        return (
          <button
            key={item.key}
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground transition-all',
              'hover:-translate-y-[1px] hover:border-brand/60 hover:shadow-sm',
              disabled && 'opacity-60',
            )}
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : item.kind === 'toolkit' ? (
              <ComposioAppIcon logo={item.toolkit.logo} name={item.label} className="h-3.5 w-3.5" />
            ) : (
              <span className="flex h-3.5 w-3.5 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5">
                {PROVIDER_ICONS_SMALL[item.integration.provider] || <FallbackIcon />}
              </span>
            )}
            <span className="truncate max-w-[120px]">{item.label}</span>
            <Plus className="h-3 w-3 text-muted-foreground" />
          </button>
        )
      })}
    </div>
  ) : null

  if (!embedded && !canManage && integrations.length === 0) return null

  const body = loading ? (
    <p className="text-sm text-muted-foreground">Carregando integrações...</p>
  ) : error ? (
    <p className="text-sm text-destructive">{error}</p>
  ) : filteredIntegrations.length > 0 ? (
    <div className="space-y-3">
      <div className="max-h-64 divide-y divide-border overflow-y-auto">
        {filteredIntegrations.map((integration) => (
          <WorkerIntegrationRow
            key={integration.id}
            integration={integration}
            canManage={canManage}
            googleCount={googleCount}
            metaCount={metaCount}
            justConnected={justConnectedIds.has(integration.id)}
            refreshing={isComposioIntegration(integration) && refreshingAccountId === integration.connected_account_id}
            disconnecting={disconnectingIds.has(integration.id)}
            busyAction={busyAction}
            rowRef={(node) => {
              if (node) rowRefs.current.set(integration.id, node)
              else rowRefs.current.delete(integration.id)
            }}
            onDisconnect={handleDisconnect}
            onRefresh={handleRefresh}
            onReconnect={handleReconnect}
            onGoogleAccounts={() => setGoogleModalOpen(true)}
            onMetaAccounts={() => setMetaModalOpen(true)}
          />
        ))}
      </div>
    </div>
  ) : (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-6 text-center">
      <div className="flex -space-x-2.5">
        {(['google', 'notion', 'googledrive', 'slack'] as const).map((provider) => (
          <div key={provider} className="flex size-10 items-center justify-center rounded-full border-2 border-border bg-background shadow-sm ring-1 ring-border/50">
            {PROVIDER_ICONS_SMALL[provider]}
          </div>
        ))}
        <div className="flex size-10 items-center justify-center rounded-full border-2 border-border bg-background text-[10px] font-semibold text-muted-foreground shadow-sm ring-1 ring-border/50">
          +90
        </div>
      </div>
      <div className="max-w-[280px] space-y-1">
        <p className="text-sm font-semibold text-foreground">Conecte suas ferramentas</p>
        <p className="text-xs text-muted-foreground">
          Gmail, Google Sheets, GitHub e dezenas de apps disponíveis para o colaborador.
        </p>
      </div>
      {canOpenConnectModal ? (
        <Button size="sm" data-testid="integrations-add-button" onClick={() => setConnectModalOpen(true)}>
          <Cable className="h-3.5 w-3.5" />
          Adicionar integração
        </Button>
      ) : null}
    </div>
  )

  return (
    <>
      {embedded ? (
        <div className="space-y-5">
          {hideHeader ? (
            integrations.length > 0 && (searchControl || addButton) && (
              <div className="flex items-center justify-end gap-3">
                {searchControl}
                {addButton}
              </div>
            )
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-semibold text-foreground">Integrações</h3>
                <p className="text-sm text-muted-foreground">Conecte as fontes de dados que o colaborador vai usar desde o início.</p>
              </div>

              <div className="flex items-center gap-3">
                {searchControl}
                {addButton}
              </div>
            </div>
          )}

          {integrations.length > 0 && quickAddSection}
          {connectError && <p className="text-sm text-destructive" role="alert">{connectError}</p>}
          {body}
        </div>
      ) : (
        <Card className="flex min-h-[380px] flex-col">
          <CardHeader>
            <CardTitle>Integrações</CardTitle>
            <CardAction className="flex items-center gap-3">
              {searchControl}
              {addButton}
            </CardAction>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col">
            {connectError && <p className="mb-3 text-sm text-destructive" role="alert">{connectError}</p>}
            {body}
          </CardContent>
        </Card>
      )}

      <IntegrationConnectModal
        open={connectModalOpen}
        onOpenChange={setConnectModalOpen}
        availableIntegrations={availableIntegrations}
        integrationToolkits={integrationToolkits}
        categories={integrationCategories}
        connectedProviders={connectedProviders}
        canManage={canManage}
        busyKey={busyAction}
        onAssignIntegration={handleAssignIntegration}
        onConnectProvider={handleConnectProvider}
        onConnectToolkit={handleConnectToolkit}
      />

      <GoogleAccountsModal
        open={googleModalOpen}
        onOpenChange={setGoogleModalOpen}
        workerId={worker.id}
        googleAdsAccounts={googleAdsAccounts}
        googleAnalyticsProperties={googleAnalyticsProperties}
      />
      <MetaAccountsModal
        open={metaModalOpen}
        onOpenChange={setMetaModalOpen}
        workerId={worker.id}
        metaAdAccounts={metaAdAccounts}
      />
    </>
  )
}
